/**
 * Iter 73 — Markups & Project Objects API helpers.
 *
 * Persistence-aware. All calls are tenant-scoped via the bearer token.
 * Errors are returned as a structured `{ error: message }`, not thrown,
 * so the UI can degrade gracefully when offline / stale.
 *
 * GH-13 (real-plan certification, 2026-09-30) — a failed read used to
 * silently become `[]` / `{total: 0}`, indistinguishable from "this
 * project genuinely has no markups/objects yet". Real saved field work
 * could appear to have vanished on a transient network blip. Every
 * list/summary function here now keeps an in-memory last-known-good
 * value per exact request key and returns THAT (not an empty default)
 * on failure, plus fires a `forgedops:read-degraded` event so a caller
 * can show a "couldn't refresh — showing last saved version" notice.
 *
 * R-06 (independent recheck, 2026-10-01) — GH-13 reopened. Two real
 * gaps in the above:
 *   1. `_lastGood` was keyed only by project/sheet/filter, NOT by
 *      authenticated identity. On a shared device (or an account
 *      switch within the same tab), a forbidden/expired response for
 *      the NEW identity fell back to the PREVIOUS identity's cached
 *      rows for that same project/sheet — real data belonging to one
 *      user could be shown to another who never had access.
 *   2. Every error, including 401/403 (access denied), was treated as
 *      an equally-transient "blip" eligible for the stale fallback. A
 *      403 means the current identity is NOT authorized — falling
 *      back to ANY previously-cached rows (even this same identity's
 *      own prior rows, e.g. after a since-revoked sheet assignment) is
 *      wrong; it must show an explicit "access denied" state instead.
 * Repair: cache keys are now prefixed by the current bearer token (so
 * a login/logout/account-switch can never cross-serve), the cache is
 * fully cleared on logout and on the existing `fop:session-expired`
 * event, and 401/403/404 responses purge that key and dispatch a
 * distinct `kind: "forbidden"` event instead of ever falling back to
 * cached rows (404 is included because this backend also uses it for
 * cross-tenant resource isolation — confirmed empirically: a
 * different tenant's user against a real sheet id gets 404, not 403,
 * and must be treated with the same "never serve cached data" rule).
 * Non-auth failures keep the original stale-fallback behavior (now
 * tagged `kind: "stale"`) rather than removing real degraded-mode
 * usefulness.
 */
import { api } from "./api";

const _lastGood = new Map();

// R-06 — identity suffix for cache keys. Doesn't need to decode the
// JWT; any two different tokens producing different suffixes is
// sufficient to guarantee no cross-identity cache hit.
function _identityTag() {
  try {
    const t = localStorage.getItem("fop_bearer");
    return t ? t.slice(-32) : "anon";
  } catch {
    return "anon";
  }
}

export function clearReadCache() {
  _lastGood.clear();
}
if (typeof window !== "undefined") {
  // Covers both an explicit logout (AuthContext calls this directly
  // too, for the in-tab case that fires before this listener could
  // matter) and the automatic session-expiry path (refresh failure).
  window.addEventListener("fop:session-expired", clearReadCache);
}

function _degraded(resource, projectId, sheetId, kind, error) {
  try {
    window.dispatchEvent(new CustomEvent("forgedops:read-degraded", {
      detail: { resource, projectId, sheetId, kind, error: error?.message || String(error) },
    }));
  } catch { /* non-browser / test environment */ }
}

/* ---------------- Markups ---------------- */
export async function listMarkups(projectId, sheetId) {
  const key = `${_identityTag()}:markups:${projectId}:${sheetId}`;
  try {
    const { data } = await api.get(
      `/projects/${projectId}/sheets/${sheetId}/markups`,
    );
    const rows = Array.isArray(data) ? data : [];
    _lastGood.set(key, rows);
    return rows;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403 || status === 404) {
      // Never serve cached rows through an authorization denial — not
      // this identity's own stale copy either, since the denial may
      // mean the assignment that made those rows visible was revoked.
      _lastGood.delete(key);
      _degraded("markups", projectId, sheetId, "forbidden", e);
      // B2-10 (independent recheck batch 2, 2026-10-01) — reopens
      // R-06. The `forgedops:read-degraded` window event alone only
      // ever reached `useMarkups`/`MarkupTool.jsx` — every OTHER
      // consumer of this same function just saw a plain `[]` and had
      // no way to tell it apart from a genuinely empty, verified
      // list. Flagging the returned array itself means ANY caller can
      // check it directly, with no event-listener boilerplate to
      // forget.
      const rows = [];
      rows._forbidden = true;
      return rows;
    }
    _degraded("markups", projectId, sheetId, "stale", e);
    const cached = _lastGood.get(key);
    const rows = cached ? [...cached] : [];
    rows._stale = !!cached;
    rows._degraded = !cached;
    return rows;
  }
}

export async function createMarkup(projectId, sheetId, payload) {
  const { data } = await api.post(
    `/projects/${projectId}/sheets/${sheetId}/markups`,
    payload,
  );
  return data;
}

export async function patchMarkup(markupId, fields) {
  const { data } = await api.patch(`/markups/${markupId}`, fields);
  return data;
}

export async function deleteMarkup(markupId) {
  await api.delete(`/markups/${markupId}`);
}

/* ---------------- Project Objects ---------------- */
export async function listProjectObjects(projectId, filters = {}) {
  const key = `${_identityTag()}:objects:${projectId}:${JSON.stringify(filters)}`;
  try {
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.sheet_id) params.sheet_id = filters.sheet_id;
    if (filters.q) params.q = filters.q;
    if (filters.status) params.status = filters.status;
    const { data } = await api.get(`/projects/${projectId}/objects`, { params });
    const rows = Array.isArray(data) ? data : [];
    _lastGood.set(key, rows);
    return rows;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403 || status === 404) {
      _lastGood.delete(key);
      _degraded("objects", projectId, filters.sheet_id, "forbidden", e);
      // B2-10 — same flagging as `listMarkups` above, so utility/
      // object overlays, layer counts, and inspector panels (none of
      // which ever listened for `forgedops:read-degraded`) can tell a
      // denied/failed read apart from a genuinely empty result.
      const rows = [];
      rows._forbidden = true;
      return rows;
    }
    _degraded("objects", projectId, filters.sheet_id, "stale", e);
    const cached = _lastGood.get(key);
    const rows = cached ? [...cached] : [];
    rows._stale = !!cached;
    rows._degraded = !cached;
    return rows;
  }
}

export async function createProjectObject(projectId, payload) {
  const { data } = await api.post(`/projects/${projectId}/objects`, payload);
  return data;
}

export async function patchProjectObject(objectId, fields) {
  const { data } = await api.patch(`/objects/${objectId}`, fields);
  return data;
}

export async function deleteProjectObject(objectId) {
  await api.delete(`/objects/${objectId}`);
}

export async function projectObjectsSummary(projectId) {
  const key = `${_identityTag()}:objects-summary:${projectId}`;
  try {
    const { data } = await api.get(`/projects/${projectId}/objects/summary`);
    const summary = data || { total: 0 };
    _lastGood.set(key, summary);
    return summary;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403 || status === 404) {
      _lastGood.delete(key);
      _degraded("objects-summary", projectId, null, "forbidden", e);
      return { total: 0, _forbidden: true };
    }
    _degraded("objects-summary", projectId, null, "stale", e);
    // Only fall back to a genuine previous value — if we've never
    // successfully loaded this summary, there's nothing to preserve,
    // so {total: 0} here is an honest "unknown", not a false "empty".
    const cached = _lastGood.get(key);
    if (cached) return { ...cached, _stale: true };
    return { total: 0, _degraded: true };
  }
}

/* ---------------- Measurements (rename only — POST/DELETE already wired in MeasureTool) ---------------- */
export async function patchMeasurement(measurementId, fields) {
  const { data } = await api.patch(`/measurements/${measurementId}`, fields);
  return data;
}
